import { post } from '../../networking';
import { CustomDataDto } from './utils';

const ENVIRONMENT = process.env.NEXT_PUBLIC_NENV;
const trackingEnvironments = ['production'];

export const GA_TRACKING_ID = process.env.NEXT_PUBLIC_GA_ID;

export enum GoogleEventName {
    SUNNYSIDE_M_2 = 'Sunnyside_M_2', // M2 - Med provider review checkout success event
    SUNNYSIDE_M_1 = 'Sunnyside_M_1' // M1 - Med paid subscription event
}

export type GoogleTrackingParams = {
    eventName: GoogleEventName,
    clientId: string,
    params: CustomDataDto
}

async function backendGoogleProxy(params: GoogleTrackingParams) {
    if(!trackingEnvironments.includes(ENVIRONMENT)) {
        // log to console and return
        console.log('Google CAPI', params.eventName, params);
        return;
    }

    // post
    await post('tracking/google', params);
}

export default async function gaServerEvent({
    eventName,
    user,
    params
}: {
    eventName: GoogleEventName,
    user: any,
    params: CustomDataDto
}) {
    if (!user) {
        return;
    }

    const clientId = await getCurrentClientId(user.id as string);
    if (clientId) {
        gaConfigWithPageViewEvent(user.id as string);
    }

    // send to our internal server
    await backendGoogleProxy({
        eventName,
        clientId,
        params
    });
}

// https://developers.google.com/analytics/devguides/collection/gtagjs/pages
export const gaClientPageView = (url) => {
    if(!window['gtag']) {
        return;
    }
    window['gtag']('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: url,
        send_to: GA_TRACKING_ID
    });
};

// https://developers.google.com/analytics/devguides/collection/gtagjs/events
export const gaClientEvent = ({ action, category = null, label = null, value = null, send_to = GA_TRACKING_ID }) => {
    if(!window['gtag']) {
        return;
    }

    const domains = [
        'app.sunnyside.co',
        'sunnyside.co',
        'get.sunnyside.co'
    ];

    const payload = {
        event_category: category,
        event_label: label,
        value,
        send_to,
        domains
    };
    window['gtag']('event', action, payload);
};

export const gaObject = (data: any, send_to = GA_TRACKING_ID) => {
    if(!window['gtag']) {
        return;
    }

    const payload = {
        ...data,
        send_to
    };
    window['gtag'](payload);
};

export const gaConfigWithPageViewEvent = (
    userId: string,
    send_to = GA_TRACKING_ID
) => {
    if(!window['gtag']) {
        return;
    }

    window['gtag']('config', send_to, {
        user_id: userId,
        send_page_view: true
    });
};

export const getCurrentClientId = async (
    userId: string,
    send_to = GA_TRACKING_ID
): Promise<string | undefined> => {
    if(!userId) {
        return undefined;
    }

    if (typeof window === 'undefined' || !send_to) {
        return undefined;
    }
    const promise = new Promise<string | undefined>((resolve) => {
        if(!window['gtag']) {
            resolve(undefined);
        }

        const timer = setTimeout(() => {
            resolve(undefined);
        }, 10000); // 10 seconds timeout

        try {
            window['gtag']('get', send_to, 'client_id', clientId => {
                clearTimeout(timer);
                resolve(clientId);
            });
        } catch (error) {
            clearTimeout(timer);
            resolve(undefined);
        }
        
    });
    const clientId = await promise;

    if (!clientId) {
        return undefined;
    }

    return clientId;
};
